import { useState } from 'react';
import { Check, Plus, X } from 'lucide-react';
import { ServiceCategory } from './BookingFlow';

interface ServiceSelectionProps {
  category: ServiceCategory;
  selectedServices: string[];
  onServiceChange: (services: string[]) => void;
}

// Comprehensive service list based on Petso Pro categories
const servicesByCategory: Record<ServiceCategory, string[]> = {
  veterinary: [
    'General Check-up',
    'Vaccination',
    'Surgery',
    'Dental Care',
    'Emergency Care',
    'Diagnostic Testing',
    'X-Ray & Imaging',
    'Blood Work',
    'Microchipping',
    'Spay/Neuter',
    'Wellness Exam',
    'Senior Pet Care'
  ],
  grooming: [
    'Full Grooming',
    'Bath & Brush',
    'Haircut',
    'Nail Trimming',
    'Ear Cleaning',
    'Teeth Brushing',
    'De-shedding Treatment',
    'Flea & Tick Treatment',
    'Show Grooming',
    'Mobile Grooming'
  ],
  boarding: [
    'Overnight Boarding',
    'Daycare',
    'Luxury Boarding',
    'Medical Boarding',
    'Puppy/Kitten Boarding',
    'Senior Pet Boarding',
    'Group Play Sessions',
    'Private Suites'
  ],
  training: [
    'Puppy Training',
    'Basic Obedience',
    'Advanced Training',
    'Behavior Modification',
    'Agility Training',
    'Service Dog Training',
    'Socialization Classes',
    'Private Sessions',
    'Group Classes',
    'Potty Training'
  ],
  walking: [
    'Daily Walk',
    'Group Walk',
    'Adventure Hike',
    'Running Partner',
    'Senior Pet Walk',
    'Puppy Walk',
    'Pet Sitting',
    'Drop-in Visits'
  ],
  breeding: [
    'Breeding Consultation',
    'Stud Service',
    'Pregnancy Care',
    'Whelping Support',
    'Puppy/Kitten Care',
    'Genetic Testing',
    'Health Screening'
  ],
  retail: [
    'Pet Food',
    'Toys & Accessories',
    'Beds & Furniture',
    'Grooming Supplies',
    'Health Products',
    'Training Equipment',
    'Clothing & Apparel',
    'Travel Accessories'
  ],
  nutrition: [
    'Diet Consultation',
    'Meal Planning',
    'Weight Management',
    'Special Diet',
    'Supplement Advice',
    'Raw Food Guidance',
    'Allergy Management'
  ],
  transportation: [
    'Airport Transport',
    'Vet Visits',
    'Grooming Transport',
    'Boarding Pick-up/Drop-off',
    'Long Distance Travel',
    'Emergency Transport'
  ],
  insurance: [
    'Pet Insurance Quote',
    'Policy Consultation',
    'Claims Assistance',
    'Coverage Review'
  ],
  identification: [
    'Microchip Registration',
    'Pet ID Tags',
    'GPS Tracker Setup',
    'Lost Pet Services'
  ],
  endoflife: [
    'End of Life Consultation',
    'Euthanasia Services',
    'Cremation',
    'Memorial Services',
    'Grief Support',
    'Paw Print Keepsakes'
  ],
  rescue: [
    'Adoption Services',
    'Foster Care',
    'Rescue Support',
    'Rehabilitation'
  ],
  events: [
    'Pet Birthday Party',
    'Training Workshop',
    'Adoption Event',
    'Pet Fashion Show',
    'Community Gathering'
  ],
  education: [
    'Pet Care Workshop',
    'First Aid Training',
    'Breed Information',
    'Behavior Seminar',
    'Nutrition Class'
  ]
};

export function ServiceSelection({ category, selectedServices, onServiceChange }: ServiceSelectionProps) {
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [otherService, setOtherService] = useState('');
  
  const services = servicesByCategory[category] || [];
  const isAllSelected = selectedServices.includes('all');

  const handleServiceToggle = (service: string) => {
    if (service === 'all') {
      if (isAllSelected) {
        onServiceChange([]);
      } else {
        onServiceChange(['all']);
      }
    } else {
      let newServices = [...selectedServices.filter(s => s !== 'all')];
      
      if (newServices.includes(service)) {
        newServices = newServices.filter(s => s !== service);
      } else {
        newServices.push(service);
      }
      
      onServiceChange(newServices);
    }
  };

  const handleAddOther = () => {
    if (otherService.trim()) {
      const newServices = [...selectedServices.filter(s => s !== 'all'), otherService.trim()];
      onServiceChange(newServices);
      setOtherService('');
      setShowOtherInput(false);
    }
  };

  const handleRemoveCustomService = (service: string) => {
    onServiceChange(selectedServices.filter(s => s !== service));
  };

  const customServices = selectedServices.filter(s => s !== 'all' && !services.includes(s));

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl mb-2 dark:text-white" style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
          What service do you need?
        </h3>
        <p className="text-gray-600 dark:text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
          Select one or more services. This helps us show you the most relevant professionals.
        </p>
      </div>

      {/* All Option */}
      <div
        onClick={() => handleServiceToggle('all')}
        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
          isAllSelected
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
            : 'border-gray-200 dark:border-white/10 hover:border-blue-300 dark:hover:border-blue-500/50'
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${
                isAllSelected
                  ? 'bg-blue-500 border-blue-500'
                  : 'border-gray-300 dark:border-white/30'
              }`}
            >
              {isAllSelected && <Check className="w-4 h-4 text-white" />}
            </div>
            <div>
              <div className="font-semibold dark:text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                All Services
              </div>
              <div className="text-sm text-gray-600 dark:text-white/60" style={{ fontFamily: 'Inter, sans-serif' }}>
                Show all professionals in this category
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Service List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {services.map((service) => {
          const isSelected = selectedServices.includes(service);
          
          return (
            <div
              key={service}
              onClick={() => handleServiceToggle(service)}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                isSelected
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                  : 'border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20'
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    isSelected
                      ? 'bg-green-500 border-green-500'
                      : 'border-gray-300 dark:border-white/30'
                  }`}
                >
                  {isSelected && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className="font-medium dark:text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {service}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Custom Services */}
      {customServices.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-semibold dark:text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
            Custom Services
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {customServices.map((service) => (
              <div
                key={service}
                className="p-4 rounded-lg border-2 border-purple-500 bg-purple-50 dark:bg-purple-900/20"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 bg-purple-500 border-purple-500">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                    <span className="font-medium dark:text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {service}
                    </span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveCustomService(service);
                    }}
                    className="p-1 hover:bg-purple-100 dark:hover:bg-purple-800/30 rounded transition-colors"
                  >
                    <X className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Others / Custom Input */}
      <div className="space-y-3">
        {!showOtherInput ? (
          <button
            onClick={() => setShowOtherInput(true)}
            className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-white/20 rounded-lg hover:border-blue-400 dark:hover:border-blue-500 transition-colors w-full"
          >
            <Plus className="w-5 h-5 text-gray-600 dark:text-white/70" />
            <span className="font-medium text-gray-700 dark:text-white/80" style={{ fontFamily: 'Inter, sans-serif' }}>
              Add Other Service (Custom)
            </span>
          </button>
        ) : (
          <div className="p-4 border-2 border-blue-300 dark:border-blue-500/50 rounded-lg bg-blue-50 dark:bg-blue-900/20">
            <label className="block text-sm font-semibold mb-2 dark:text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
              Custom Service Name
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={otherService}
                onChange={(e) => setOtherService(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddOther()}
                placeholder="Enter service name..."
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg dark:bg-[#0F0E16] dark:text-white"
                style={{ fontFamily: 'Inter, sans-serif' }}
                autoFocus
              />
              <button
                onClick={handleAddOther}
                disabled={!otherService.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Add
              </button>
              <button
                onClick={() => {
                  setShowOtherInput(false);
                  setOtherService('');
                }}
                className="px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 dark:text-white transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Selection Summary */}
      {selectedServices.length > 0 && !isAllSelected && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-500/30 rounded-lg">
          <div className="flex items-start gap-2">
            <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-semibold text-green-900 dark:text-green-100" style={{ fontFamily: 'Sora, sans-serif' }}>
                {selectedServices.length} service{selectedServices.length > 1 ? 's' : ''} selected
              </div>
              <div className="text-sm text-green-700 dark:text-green-300 mt-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                We'll show you professionals who offer: {selectedServices.join(', ')}
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedServices.length === 0 && (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30 rounded-lg">
          <p className="text-sm text-blue-800 dark:text-blue-200" style={{ fontFamily: 'Inter, sans-serif' }}>
            💡 <strong>Tip:</strong> Select at least one service to see relevant professionals, or choose "All Services" to browse all options.
          </p>
        </div>
      )}
    </div>
  );
}
